import { Pill } from '@/components/Pill';
import {
  sortInventoryWatch,
  stockStatus,
  useHomeInventoryWatch,
  weeksBarFraction,
} from '@/lib/useHomeDashboard';
import { TileMessage, TileShell, TileSkeleton } from './TileShell';
import { EXCLUDED_ITEMS_NOTE, MUTED_TEXT, NOT_AVAILABLE } from './tileText';

const nf = new Intl.NumberFormat('en-US');

const TONE_CRITICAL = 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
const TONE_WATCH = 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';

const TH = `px-2 py-2 text-left text-[12px] font-semibold uppercase tracking-caps-pill ${MUTED_TEXT}`;
const TD = 'px-2 py-2 text-[13px] text-amp-ink dark:text-amp-dark-ink';

export function InventoryWatch() {
  const { data, isLoading, unavailable } = useHomeInventoryWatch();
  const items = sortInventoryWatch(data?.items ?? []);

  return (
    <TileShell
      label="Inventory"
      title="Inventory watch"
      caption="Items running low in stores (under 2 weeks of stock). Not affected by the date filter."
    >
      {isLoading ? (
        <TileSkeleton rows={5} />
      ) : unavailable ? (
        <TileMessage>{NOT_AVAILABLE}</TileMessage>
      ) : items.length === 0 ? (
        <TileMessage>Nothing is running low right now.</TileMessage>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr className="border-b border-amp-line dark:border-amp-dark-line">
                <th scope="col" className={TH}>Item</th>
                <th scope="col" className={TH}>Stores low</th>
                <th scope="col" className={`${TH} text-right`}>On hand</th>
                <th scope="col" className={`${TH} text-right`}>Sold / week</th>
                <th scope="col" className={TH}>Weeks left</th>
                <th scope="col" className={TH}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => {
                const status = stockStatus(i.weeks_left);
                return (
                  <tr
                    key={i.item_no}
                    className="border-b border-amp-line last:border-b-0 dark:border-amp-dark-line"
                  >
                    <td className={TD}>
                      <div className="font-semibold">{i.descr ?? i.item_no}</div>
                      {i.descr && <div className={`text-[12px] ${MUTED_TEXT}`}>{i.item_no}</div>}
                    </td>
                    <td className={TD}>
                      {i.stores_low} of {i.stores_carrying}
                    </td>
                    <td className={`${TD} text-right tabular-nums`}>{nf.format(i.on_hand)}</td>
                    <td className={`${TD} text-right tabular-nums`}>{nf.format(i.weekly_units)}</td>
                    <td className={TD}>
                      <div className="flex items-center gap-2">
                        <span className="w-8 tabular-nums">
                          {i.weeks_left == null ? '--' : i.weeks_left.toFixed(1)}
                        </span>
                        <div className="h-2 w-16 rounded bg-amp-cream dark:bg-amp-dark-surface-warm">
                          <div
                            className={`h-full rounded ${
                              status.critical ? 'bg-red-600 dark:bg-red-400' : 'bg-amber-500 dark:bg-amber-400'
                            }`}
                            style={{ width: `${weeksBarFraction(i.weeks_left) * 100}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className={TD}>
                      <Pill tone={status.critical ? TONE_CRITICAL : TONE_WATCH}>{status.label}</Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className={`mt-3 text-[12px] ${MUTED_TEXT}`}>{EXCLUDED_ITEMS_NOTE}</p>
    </TileShell>
  );
}
